import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  Consent,
  CreateConsentInput,
  ConsentFilters,
  AccessLog,
  PaymentInitiation,
  InitiatePaymentInput,
  PaymentFilters,
  OpenFinanceConfig,
  UpdateOpenFinanceConfigInput,
  ConsentStatistics,
  AccessStatistics,
} from '../types/open-finance';

// ==================== Consents ====================

export function useConsents(filters?: ConsentFilters) {
  return useQuery({
    queryKey: ['consents', filters],
    queryFn: () =>
      api
        .get<{ consents: Consent[]; total: number }>('/open-finance/consents', {
          params: filters,
        })
        .then((r) => r.data),
  });
}

export function useMyConsents(filters?: ConsentFilters) {
  return useQuery({
    queryKey: ['my-consents', filters],
    queryFn: () =>
      api
        .get<{ consents: Consent[]; total: number }>('/open-finance/consents/my', {
          params: filters,
        })
        .then((r) => r.data),
  });
}

export function useConsent(consentId: string) {
  return useQuery({
    queryKey: ['consent', consentId],
    queryFn: () =>
      api.get<Consent>(`/open-finance/consents/${consentId}`).then((r) => r.data),
    enabled: !!consentId,
  });
}

export function useCreateConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConsentInput) =>
      api.post<Consent>('/open-finance/consents', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
    },
  });
}

export function useAuthorizeConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (consentId: string) =>
      api
        .post<Consent>(`/open-finance/consents/${consentId}/authorize`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['my-consents'] });
    },
  });
}

export function useRevokeConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ consentId, reason }: { consentId: string; reason?: string }) =>
      api
        .post<Consent>(`/open-finance/consents/${consentId}/revoke`, { reason })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['my-consents'] });
    },
  });
}

// ==================== Access Logs ====================

export function useConsentAccessLog(consentId: string) {
  return useQuery({
    queryKey: ['consent-access-log', consentId],
    queryFn: () =>
      api
        .get<AccessLog[]>(`/open-finance/consents/${consentId}/access-log`)
        .then((r) => r.data),
    enabled: !!consentId,
  });
}

// ==================== Data Sharing ====================

export function useSharedAccounts(consentId: string) {
  return useQuery({
    queryKey: ['shared-accounts', consentId],
    queryFn: () =>
      api
        .get(`/open-finance/data/accounts`, { params: { consentId } })
        .then((r) => r.data),
    enabled: !!consentId,
  });
}

export function useSharedTransactions(consentId: string, filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: ['shared-transactions', consentId, filters],
    queryFn: () =>
      api
        .get(`/open-finance/data/transactions`, { params: { consentId, ...filters } })
        .then((r) => r.data),
    enabled: !!consentId,
  });
}

// ==================== Payment Initiation ====================

export function usePayments(filters?: PaymentFilters) {
  return useQuery({
    queryKey: ['open-finance-payments', filters],
    queryFn: () =>
      api
        .get<{ payments: PaymentInitiation[]; total: number }>('/open-finance/payments', {
          params: filters,
        })
        .then((r) => r.data),
  });
}

export function usePayment(paymentId: string) {
  return useQuery({
    queryKey: ['open-finance-payment', paymentId],
    queryFn: () =>
      api.get<PaymentInitiation>(`/open-finance/payments/${paymentId}`).then((r) => r.data),
    enabled: !!paymentId,
  });
}

export function useInitiatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InitiatePaymentInput) =>
      api.post<PaymentInitiation>('/open-finance/payments', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['open-finance-payments'] });
    },
  });
}

export function useCancelPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: string) =>
      api.delete<PaymentInitiation>(`/open-finance/payments/${paymentId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['open-finance-payments'] });
    },
  });
}

// ==================== Config (Admin) ====================

export function useOpenFinanceConfig() {
  return useQuery({
    queryKey: ['open-finance-config'],
    queryFn: () =>
      api.get<OpenFinanceConfig>('/open-finance/admin/config').then((r) => r.data),
  });
}

export function useUpdateOpenFinanceConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOpenFinanceConfigInput) =>
      api.put<OpenFinanceConfig>('/open-finance/admin/config', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['open-finance-config'] });
    },
  });
}

// ==================== Statistics (Admin) ====================

export function useConsentStatistics() {
  return useQuery({
    queryKey: ['consent-statistics'],
    queryFn: () =>
      api.get<ConsentStatistics>('/open-finance/admin/statistics/consents').then((r) => r.data),
  });
}

export function useAccessStatistics(period?: '7d' | '30d' | '90d') {
  return useQuery({
    queryKey: ['access-statistics', period],
    queryFn: () =>
      api
        .get<AccessStatistics>('/open-finance/admin/statistics/access', {
          params: { period },
        })
        .then((r) => r.data),
  });
}
